import React, { useEffect, useState } from "react";
import { SITE } from "../lib/site";
import WhatsAppIcon from "./icons/WhatsAppIcon";
import { Copy, Check, X, Phone, Mail } from "lucide-react";

/* ---------- shared icon button shell ---------- */
const variantClasses = {
  dark:
    "border border-white/25 text-paper/75 hover:border-voltage hover:text-voltage",
  light:
    "border border-[var(--border-light)] text-ink/75 hover:border-electric hover:text-electric",
};

/* ---------- Reveal modal (shows the value, doesn't auto-launch app) ---------- */
function ContactRevealModal({ open, onClose, kind, value, displayValue, label }) {
  const [copied, setCopied] = useState(false);

  // Close on Escape + lock background scroll while open
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  useEffect(() => {
    if (!open) setCopied(false);
  }, [open]);

  if (!open) return null;

  const display = displayValue || value;
  const KindIcon = kind === "phone" ? Phone : Mail;
  const titleText = label || (kind === "phone" ? "Phone" : "Email");

  const handleCopy = async () => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        // Fallback
        const ta = document.createElement("textarea");
        ta.value = value;
        ta.setAttribute("readonly", "");
        ta.style.position = "absolute";
        ta.style.left = "-9999px";
        document.body.appendChild(ta);
        ta.select();
        document.execCommand("copy");
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* no-op */
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={titleText}
      data-testid="contact-reveal-modal"
    >
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-ink/60 backdrop-blur-sm"
      />
      <div className="relative w-full max-w-md bg-paper rounded-xl shadow-2xl border border-[var(--border-light)] p-6 md:p-7">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-3 right-3 inline-flex items-center justify-center w-9 h-9 rounded-md text-ink/60 hover:text-ink hover:bg-ink/5 transition-colors"
        >
          <X size={18} strokeWidth={1.8} />
        </button>

        <div className="flex items-center gap-3">
          <span className="inline-flex items-center justify-center w-10 h-10 rounded-md bg-voltage/15 text-ink border border-voltage/40">
            <KindIcon size={18} strokeWidth={1.6} />
          </span>
          <div className="text-[11px] tracking-[0.16em] uppercase text-ink/55 font-medium">
            {titleText}
          </div>
        </div>

        <div
          data-testid="contact-reveal-value"
          className="mt-5 break-all font-display text-[24px] md:text-[28px] leading-tight tracking-tight text-ink select-all"
        >
          {display}
        </div>

        <button
          type="button"
          onClick={handleCopy}
          data-testid="contact-reveal-copy"
          className="mt-6 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md text-[14px] font-medium border border-ink/15 text-ink hover:border-ink hover:bg-ink hover:text-paper transition-colors"
        >
          {copied ? <Check size={16} strokeWidth={2} /> : <Copy size={16} strokeWidth={2} />}
          <span>{copied ? "Copied" : "Copy"}</span>
        </button>
      </div>
    </div>
  );
}

/* ---------- Reveal-on-click button (used for tel & email) ---------- */
function RevealIconButton({ children, label, testId, variant = "dark", className = "", kind, value, displayValue, modalLabel }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        aria-label={label}
        data-testid={testId}
        onClick={() => setOpen(true)}
        className={`inline-flex items-center justify-center w-[52px] h-[52px] rounded-md transition-all duration-300 ${
          variantClasses[variant] || variantClasses.dark
        } ${className}`}
      >
        {children}
      </button>
      <ContactRevealModal
        open={open}
        onClose={() => setOpen(false)}
        kind={kind}
        value={value}
        displayValue={displayValue}
        label={modalLabel}
      />
    </>
  );
}

/* ---------- External-link icon button (LinkedIn) ---------- */
function LinkIconButton({ children, href, label, testId, variant = "light", className = "" }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      data-testid={testId}
      className={`inline-flex items-center justify-center w-[52px] h-[52px] rounded-md transition-all duration-300 ${
        variantClasses[variant] || variantClasses.dark
      } ${className}`}
    >
      {children}
    </a>
  );
}

/* ---------- Office phone (uses SITE.phoneTel) ---------- */
export function PhoneIconBtn({ variant = "dark", testId = "icon-phone", className = "" }) {
  return (
    <RevealIconButton
      variant={variant}
      label="View phone number"
      testId={testId}
      className={className}
      kind="phone"
      value={SITE.phoneTel}
      displayValue={SITE.phoneDisplay}
      modalLabel="Office phone"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
      </svg>
    </RevealIconButton>
  );
}

/* ---------- Office email ---------- */
export function MailIconBtn({ variant = "dark", testId = "icon-mail", className = "" }) {
  return (
    <RevealIconButton
      variant={variant}
      label="View email address"
      testId={testId}
      className={className}
      kind="email"
      value={SITE.email}
      displayValue={SITE.email}
      modalLabel="Office email"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect width="20" height="16" x="2" y="4" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
      </svg>
    </RevealIconButton>
  );
}

/* ---------- LinkedIn (true external link) ---------- */
export function LinkedinIconBtn({ href, variant = "light", testId = "icon-linkedin", label = "LinkedIn", className = "" }) {
  return (
    <LinkIconButton href={href} variant={variant} label={label} testId={testId} className={className}>
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-4 0v7h-4v-7a6 6 0 0 1 6-6Z" />
        <rect width="4" height="12" x="2" y="9" />
        <circle cx="4" cy="4" r="2" />
      </svg>
    </LinkIconButton>
  );
}

/* ---------- Partner phone (per partner) ---------- */
export function PartnerPhoneIconBtn({ phoneTel, phoneDisplay, variant = "light", testId, className = "" }) {
  if (!phoneTel) return null;
  return (
    <RevealIconButton
      variant={variant}
      label={`View phone number (${phoneDisplay || phoneTel})`}
      testId={testId}
      className={className}
      kind="phone"
      value={phoneTel}
      displayValue={phoneDisplay}
      modalLabel="Partner phone"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92Z" />
      </svg>
    </RevealIconButton>
  );
}

/* ---------- Partner email (per partner) ---------- */
export function PartnerMailIconBtn({ email, variant = "light", testId, className = "" }) {
  return (
    <RevealIconButton
      variant={variant}
      label={`View email (${email})`}
      testId={testId}
      className={className}
      kind="email"
      value={email}
      displayValue={email}
      modalLabel="Partner email"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <rect width="20" height="16" x="2" y="4" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
      </svg>
    </RevealIconButton>
  );
}

/* ---------- WhatsApp icon (kept as direct link — that's the explicit user intent) ---------- */
export function WhatsAppIconBtn({ variant = "voltage", testId = "icon-whatsapp", className = "" }) {
  if (variant === "voltage") {
    return (
      <a
        href={SITE.whatsappUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
        data-testid={testId}
        className={`voltage-cta inline-flex items-center justify-center w-[52px] h-[52px] rounded-md ${className}`}
      >
        <WhatsAppIcon size={22} strokeWidth={2} />
      </a>
    );
  }
  return (
    <LinkIconButton
      variant={variant}
      href={SITE.whatsappUrl}
      label="Chat on WhatsApp"
      testId={testId}
      className={className}
    >
      <WhatsAppIcon size={22} strokeWidth={2} />
    </LinkIconButton>
  );
}
